import { StyleSheet, ViewStyle } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export interface FeatureFlagStyles {
  valueBadge: ViewStyle;
  valueBadgeDefault: ViewStyle;
  valueBadgeTrue: ViewStyle;
  valueBadgeFalse: ViewStyle;
  valueBadgeCustom: ViewStyle;
  valueBadgeText: { fontSize: number; fontWeight: '500'; color: string };
}

/**
 * Theme-aware feature-flag styles. Mirrors Phase 3.2 hook conversion (see
 * `sharedFormStyles.ts`). Replaces the previously static `styles` export
 * that referenced legacy admin color tokens.
 */
export function useFeatureFlagStyles() {
  return useThemedStyles(({ semantic, colors }) => {
    const tints = {
      brand: withAlpha(colors.accent.blueBg, 0.2),
      success: withAlpha(semantic.status.success, 0.2),
      error: withAlpha(semantic.status.error, 0.2),
      warning: withAlpha(semantic.status.warning, 0.2),
    };

    return StyleSheet.create({
      valueBadge: {
        alignSelf: 'flex-start',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
      },
      valueBadgeDefault: {
        backgroundColor: tints.brand,
      },
      valueBadgeTrue: {
        backgroundColor: tints.success,
      },
      valueBadgeFalse: {
        backgroundColor: tints.error,
      },
      valueBadgeCustom: {
        backgroundColor: tints.warning,
      },
      valueBadgeText: {
        fontSize: 12,
        fontWeight: '500' as const,
        color: colors.text.primary,
      },
    });
  });
}

/**
 * Picks a value badge style key based on whether the current value matches
 * the default and what its boolean state is. Caller passes the styles bag
 * from `useFeatureFlagStyles()` so this stays a pure helper.
 */
export function getValueStyleKey(
  currentValue: any,
  defaultValue: any,
): 'valueBadgeDefault' | 'valueBadgeTrue' | 'valueBadgeFalse' | 'valueBadgeCustom' {
  if (currentValue === defaultValue) return 'valueBadgeDefault';
  if (typeof currentValue === 'boolean') {
    return currentValue ? 'valueBadgeTrue' : 'valueBadgeFalse';
  }
  return 'valueBadgeCustom';
}

function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  return color;
}
